import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { salePipelineStages } from "../../data/ticketSalesPipeline/constants";
import { useChannels } from "../../hooks/useChannels";
import { useAgents } from "../../hooks/useAgents";
import { useUsers } from "../../hooks/useUsers";
import { getChannelTemplates } from "../../api/client";
import { isWhatsappTemplateUsable } from "../../utils/whatsappTemplateLifecycle";
import AssigneeTransferField from "../AssigneeTransferField";
import LoggedUsersFallbackField from "../LoggedUsersFallbackField";
import {
  selectionRequiresLoggedUsersOnlyDisabled,
  LOGGED_USERS_ONLY_FIELD,
} from "../../utils/assigneeTransferField";
import { applyLoggedUsersOnlyFlag } from "../../utils/loggedUsersFallback";
import {
  handoffTargetToLegacyValues,
  handoffTargetToSelection,
  selectionToHandoffTarget,
} from "../../utils/passagemRecoveryHandoff";
import FaqBusinessHoursSection from "./FaqBusinessHoursSection";

const DEFAULT_STAGES = [6, 8, 9, 10];
const RECOVERY_PRIMARY_STAGES = [6, 7, 8, 9, 10];
const RECOVERY_EXTENDED_STAGES = [1, 2, 3, 4, 5, 14];

const STAGE_BY_ID = Object.fromEntries(salePipelineStages.map((s) => [s.id, s]));

function RecoveryConfigAccordion({ title, subtitle, icon: Icon, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        mb: 1.5,
        "&.Mui-expanded": { mb: 1.5 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 52, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ pr: 1 }}>
          {Icon ? <Icon fontSize="small" color="action" sx={{ mt: 0.25 }} /> : null}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

function RecoveryModeCard({ value, current, title, description, readOnly, onSelect }) {
  const selected = current === value;
  return (
    <Paper
      variant="outlined"
      onClick={() => !readOnly && onSelect(value)}
      sx={{
        p: 1.5,
        cursor: readOnly ? "default" : "pointer",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.selected" : "background.paper",
        transition: "border-color 0.15s, background-color 0.15s",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Radio checked={selected} value={value} disabled={readOnly} sx={{ p: 0.5, mt: -0.25 }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, lineHeight: 1.45 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function ChannelTemplatesFields({ channelId, templates, onChange, readOnly, t }) {
  const { data: tplData, isLoading, isError } = useQuery(
    ["channel-templates", channelId],
    () => getChannelTemplates(channelId),
    { enabled: Boolean(channelId), staleTime: 60_000 }
  );
  const tplList = (
    Array.isArray(tplData?.templates)
      ? tplData.templates
      : Array.isArray(tplData?.data?.templates)
        ? tplData.data.templates
        : []
  ).filter(isWhatsappTemplateUsable);

  const fields = [
    {
      key: "first_contact",
      label: t("agentsListPage.recoveryTplFirstContact"),
      hint: t("agentsListPage.recoveryTplFirstContactHint"),
    },
    {
      key: "follow_up",
      label: t("agentsListPage.recoveryTplFollowUp"),
      hint: t("agentsListPage.recoveryTplFollowUpHint"),
    },
    {
      key: "no_availability",
      label: t("agentsListPage.recoveryTplNoAvailability"),
      hint: t("agentsListPage.recoveryTplNoAvailabilityHint"),
    },
    {
      key: "payment_reminder",
      label: t("agentsListPage.recoveryTplPaymentReminder"),
      hint: t("agentsListPage.recoveryTplPaymentReminderHint"),
    },
  ];

  const configuredCount = fields.filter(({ key }) => templates?.[key]).length;

  if (!channelId) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t("agentsListPage.recoveryTplSelectChannelFirst")}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {t("agentsListPage.recoveryTplSectionTitle")}
        </Typography>
        {configuredCount > 0 ? (
          <Chip size="small" label={t("agentsListPage.recoveryTplConfigured", { count: configuredCount })} />
        ) : null}
      </Stack>
      {isError ? (
        <Alert severity="warning" sx={{ py: 0.25, mb: 1 }}>
          {t("agentsListPage.recoveryTplLoadError")}
        </Alert>
      ) : null}
      {!isLoading && !tplList.length ? (
        <Alert severity="info" sx={{ py: 0.25, mb: 1 }}>
          {t("agentsListPage.recoveryTplEmpty")}
        </Alert>
      ) : null}
      <Stack spacing={1.5}>
        {fields.map(({ key, label, hint }) => (
          <FormControl key={key} fullWidth size="small" disabled={readOnly}>
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              value={templates?.[key] || ""}
              onChange={(e) => onChange({ ...templates, [key]: e.target.value })}
            >
              <MenuItem value="">
                <em>{isLoading ? t("agentsListPage.recoveryTplLoading") : t("agentsListPage.recoveryTplNone")}</em>
              </MenuItem>
              {tplList.map((tpl) => (
                <MenuItem key={tpl.id || tpl.template_id} value={String(tpl.id || tpl.template_id)}>
                  {tpl.name || tpl.element_name || tpl.id}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{hint}</FormHelperText>
          </FormControl>
        ))}
      </Stack>
    </Box>
  );
}

function StageTimingFields({ stageId, rule, skillConfig, readOnly, t, onUpdate }) {
  const idle = rule.idle_minutes ?? skillConfig.default_idle_minutes ?? 30;
  const attempts = rule.max_attempts ?? skillConfig.default_max_attempts ?? 2;
  const hasOverride =
    rule.idle_minutes != null ||
    rule.max_attempts != null;

  return (
    <Box
      sx={{
        ml: { xs: 0, sm: 4 },
        mt: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "action.hover",
        border: 1,
        borderColor: hasOverride ? "primary.light" : "divider",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t("agentsListPage.recoveryStageTimingTitle")}
        </Typography>
        {hasOverride ? <Chip size="small" label={t("agentsListPage.recoveryStageCustomized")} color="primary" variant="outlined" /> : null}
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          label={t("agentsListPage.recoveryIdleMinutes")}
          helperText={t("agentsListPage.recoveryIdleMinutesHelper")}
          size="small"
          value={idle}
          onChange={(e) => onUpdate(stageId, { idle_minutes: e.target.value.replace(/\D/g, "") })}
          disabled={readOnly}
          sx={{ flex: 1, maxWidth: { sm: 200 } }}
        />
        <TextField
          label={t("agentsListPage.recoveryMaxAttempts")}
          helperText={t("agentsListPage.recoveryMaxAttemptsHelper")}
          size="small"
          value={attempts}
          onChange={(e) => onUpdate(stageId, { max_attempts: e.target.value.replace(/\D/g, "") })}
          disabled={readOnly}
          sx={{ flex: 1, maxWidth: { sm: 200 } }}
        />
      </Stack>
    </Box>
  );
}

function StageRow({ stage, checked, rule, skillConfig, readOnly, t, onToggle, onUpdateRule }) {
  const hintKey = `agentsListPage.recoveryStageHint${stage.id}`;
  const hint = t(hintKey, { defaultValue: "" });

  return (
    <Box sx={{ py: 0.75 }}>
      <FormControlLabel
        sx={{ alignItems: "flex-start", m: 0 }}
        control={
          <Checkbox
            checked={checked}
            onChange={() => onToggle(stage.id)}
            disabled={readOnly}
            sx={{ pt: 0.25 }}
          />
        }
        label={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400 }}>
              {stage.name}
            </Typography>
            {hint ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                {hint}
              </Typography>
            ) : null}
          </Box>
        }
      />
      {checked ? (
        <StageTimingFields
          stageId={stage.id}
          rule={rule}
          skillConfig={skillConfig}
          readOnly={readOnly}
          t={t}
          onUpdate={onUpdateRule}
        />
      ) : null}
    </Box>
  );
}

export default function PassagemRecoveryConfigSection({
  skillConfig = {},
  mergeRecoveryConfig,
  readOnly = false,
  currentAgentId,
}) {
  const { t } = useTranslation("settings");
  const [showExtendedStages, setShowExtendedStages] = useState(false);

  const { data: channelsData } = useChannels(1, 200, "");
  const { data: agentsQuery, isLoading: loadingAgents } = useAgents(1, 100, "");
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 200, "", "todos");
  const channels = channelsData?.channels || [];
  const agents = agentsQuery?.agents || [];
  const users = usersData?.users ?? [];

  const handoffAgents = useMemo(
    () =>
      (Array.isArray(agents) ? agents : []).filter(
        (a) =>
          String(a.id) !== String(currentAgentId || "") &&
          (a.status === "active" || a.status === true)
      ),
    [agents, currentAgentId]
  );

  const handoffSelection = useMemo(
    () => handoffTargetToSelection(skillConfig.handoff_target, { users, agents: handoffAgents }),
    [skillConfig.handoff_target, users, handoffAgents]
  );

  const disableLoggedUsersOnly = selectionRequiresLoggedUsersOnlyDisabled(handoffSelection);
  const loggedUsersOnly = skillConfig.handoff_target?.logged_users_only === true;

  const handoffLegacyValues = useMemo(
    () => ({
      ...handoffTargetToLegacyValues(skillConfig.handoff_target, { users, agents: handoffAgents }),
      [LOGGED_USERS_ONLY_FIELD]: loggedUsersOnly ? "sim" : "",
    }),
    [skillConfig.handoff_target, users, handoffAgents, loggedUsersOnly]
  );

  const targetStages = Array.isArray(skillConfig.target_stages) ? skillConfig.target_stages : DEFAULT_STAGES;
  const stageRules = skillConfig.stage_rules && typeof skillConfig.stage_rules === "object" ? skillConfig.stage_rules : {};
  const channelRows = Array.isArray(skillConfig.channels) ? skillConfig.channels : [];
  const onReplyMode = skillConfig.on_reply_mode || "close_sale";
  const channelStrategy = skillConfig.channel_strategy || "prefer_purchase_channel";

  const activeStageCount = targetStages.length;

  const strategyHelperKey = {
    prefer_purchase_channel: "agentsListPage.recoveryStrategyPurchaseHelper",
    priority_order: "agentsListPage.recoveryStrategyPriorityHelper",
    best_window: "agentsListPage.recoveryStrategyWindowHelper",
  }[channelStrategy];

  const applyHandoffSelection = (selection) => {
    mergeRecoveryConfig({
      handoff_target: selectionToHandoffTarget(selection, {
        logged_users_only: loggedUsersOnly && !selectionRequiresLoggedUsersOnlyDisabled(selection),
        logged_users_fallback: skillConfig.handoff_target?.logged_users_fallback,
        tipo_id: skillConfig.handoff_target?.tipo_id ?? null,
      }),
    });
  };

  useEffect(() => {
    if (!disableLoggedUsersOnly || !loggedUsersOnly) return;
    mergeRecoveryConfig({
      handoff_target: applyLoggedUsersOnlyFlag(skillConfig.handoff_target || {}, false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableLoggedUsersOnly, loggedUsersOnly]);

  const toggleStage = (stageId) => {
    const set = new Set(targetStages.map(Number));
    if (set.has(stageId)) set.delete(stageId);
    else set.add(stageId);
    mergeRecoveryConfig({ target_stages: [...set].sort((a, b) => a - b) });
  };

  const updateStageRule = (stageId, partial) => {
    const key = String(stageId);
    mergeRecoveryConfig({
      stage_rules: {
        ...stageRules,
        [key]: { ...(stageRules[key] || {}), ...partial },
      },
    });
  };

  const addChannel = () => {
    const next = [
      ...channelRows,
      {
        channel_id: channels[0]?.id || "",
        priority: channelRows.length + 1,
        enabled: true,
        templates: {},
        use_free_text_when_window_open: true,
      },
    ];
    mergeRecoveryConfig({ channels: next });
  };

  const updateChannel = (idx, partial) => {
    const next = channelRows.map((row, i) => (i === idx ? { ...row, ...partial } : row));
    mergeRecoveryConfig({ channels: next });
  };

  const removeChannel = (idx) => {
    mergeRecoveryConfig({ channels: channelRows.filter((_, i) => i !== idx) });
  };

  const renderStageGroup = (stageIds) =>
    stageIds
      .map((id) => STAGE_BY_ID[id])
      .filter(Boolean)
      .map((stage) => (
        <StageRow
          key={stage.id}
          stage={stage}
          checked={targetStages.includes(stage.id)}
          rule={stageRules[String(stage.id)] || {}}
          skillConfig={skillConfig}
          readOnly={readOnly}
          t={t}
          onToggle={toggleStage}
          onUpdateRule={updateStageRule}
        />
      ));

  const intelOptions = [
    {
      key: "revalidate_availability",
      label: t("agentsListPage.recoveryRevalidateAvailability"),
      helper: t("agentsListPage.recoveryRevalidateAvailabilityHelper"),
      checked: skillConfig.revalidate_availability !== false,
    },
    {
      key: "use_purchase_history",
      label: t("agentsListPage.recoveryUseHistory"),
      helper: t("agentsListPage.recoveryUseHistoryHelper"),
      checked: skillConfig.use_purchase_history !== false,
    },
    {
      key: "suggest_alternatives_when_unavailable",
      label: t("agentsListPage.recoverySuggestAlternatives"),
      helper: t("agentsListPage.recoverySuggestAlternativesHelper"),
      checked: skillConfig.suggest_alternatives_when_unavailable !== false,
    },
    {
      key: "include_payment_link_if_valid",
      label: t("agentsListPage.recoveryIncludePaymentLink"),
      helper: t("agentsListPage.recoveryIncludePaymentLinkHelper"),
      checked: skillConfig.include_payment_link_if_valid !== false,
    },
  ];

  return (
    <Stack spacing={0}>
      <Alert severity="info" sx={{ mb: 1.5, "& .MuiAlert-message": { width: "100%" } }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.35 }}>
          {t("agentsListPage.recoveryIntroTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("agentsListPage.recoveryIntroBody")}
        </Typography>
      </Alert>

      <RecoveryConfigAccordion
        icon={TimelineOutlinedIcon}
        title={t("agentsListPage.recoveryStagesTitle")}
        subtitle={t("agentsListPage.recoveryStagesHint")}
        defaultExpanded
      >
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "action.hover" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
            {t("agentsListPage.recoveryDefaultsTitle")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label={t("agentsListPage.recoveryDefaultIdle")}
              helperText={t("agentsListPage.recoveryDefaultIdleHelper")}
              size="small"
              value={skillConfig.default_idle_minutes ?? 30}
              onChange={(e) =>
                mergeRecoveryConfig({ default_idle_minutes: e.target.value.replace(/\D/g, "") })
              }
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label={t("agentsListPage.recoveryDefaultMaxAttempts")}
              helperText={t("agentsListPage.recoveryDefaultMaxAttemptsHelper")}
              size="small"
              value={skillConfig.default_max_attempts ?? 2}
              onChange={(e) =>
                mergeRecoveryConfig({ default_max_attempts: e.target.value.replace(/\D/g, "") })
              }
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label={t("agentsListPage.recoveryRetryInterval")}
              helperText={t("agentsListPage.recoveryRetryIntervalHelper")}
              size="small"
              value={skillConfig.retry_interval_minutes ?? 120}
              onChange={(e) =>
                mergeRecoveryConfig({ retry_interval_minutes: e.target.value.replace(/\D/g, "") })
              }
              disabled={readOnly}
              fullWidth
            />
          </Stack>
        </Paper>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("agentsListPage.recoveryPrimaryStagesLabel")}
          </Typography>
          <Chip size="small" label={t("agentsListPage.recoveryActiveStages", { count: activeStageCount })} />
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t("agentsListPage.recoveryPrimaryStagesHint")}
        </Typography>
        {renderStageGroup(RECOVERY_PRIMARY_STAGES)}

        <Button
          size="small"
          onClick={() => setShowExtendedStages((v) => !v)}
          endIcon={
            <ExpandMoreIcon sx={{ transform: showExtendedStages ? "rotate(180deg)" : "none", transition: "0.2s" }} />
          }
          sx={{ mt: 1, mb: showExtendedStages ? 1 : 0 }}
        >
          {showExtendedStages
            ? t("agentsListPage.recoveryHideExtendedStages")
            : t("agentsListPage.recoveryShowExtendedStages")}
        </Button>
        <Collapse in={showExtendedStages}>{renderStageGroup(RECOVERY_EXTENDED_STAGES)}</Collapse>

        {!activeStageCount ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t("agentsListPage.recoveryNoStagesWarning")}
          </Alert>
        ) : null}
      </RecoveryConfigAccordion>

      <RecoveryConfigAccordion
        icon={ReplyOutlinedIcon}
        title={t("agentsListPage.recoveryModeTitle")}
        subtitle={t("agentsListPage.recoveryModeHint")}
      >
        <RadioGroup
          value={onReplyMode}
          onChange={(e) => mergeRecoveryConfig({ on_reply_mode: e.target.value })}
        >
          <Stack spacing={1.25}>
            <RecoveryModeCard
              value="close_sale"
              current={onReplyMode}
              title={t("agentsListPage.recoveryModeCloseSale")}
              description={t("agentsListPage.recoveryModeCloseSaleDesc")}
              readOnly={readOnly}
              onSelect={(v) => mergeRecoveryConfig({ on_reply_mode: v })}
            />
            <RecoveryModeCard
              value="reengage_handoff"
              current={onReplyMode}
              title={t("agentsListPage.recoveryModeHandoff")}
              description={t("agentsListPage.recoveryModeHandoffDesc")}
              readOnly={readOnly}
              onSelect={(v) => mergeRecoveryConfig({ on_reply_mode: v })}
            />
          </Stack>
        </RadioGroup>

        {onReplyMode === "reengage_handoff" ? (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderStyle: "dashed" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("agentsListPage.recoveryHandoffPanelTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {t("agentsListPage.recoveryHandoffPanelHint")}
            </Typography>
            <Stack spacing={1.5}>
              <AssigneeTransferField
                label={t("agentsListPage.recoveryHandoffType")}
                values={handoffLegacyValues}
                users={users}
                agents={handoffAgents}
                loading={loadingUsers || loadingAgents}
                disabled={readOnly}
                helperText={t("agentsListPage.recoveryHandoffHelper")}
                onApply={(selection) => applyHandoffSelection(selection)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={loggedUsersOnly}
                    disabled={readOnly || disableLoggedUsersOnly}
                    onChange={(e) =>
                      mergeRecoveryConfig({
                        handoff_target: applyLoggedUsersOnlyFlag(
                          skillConfig.handoff_target || {},
                          e.target.checked
                        ),
                      })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{t("agentsListPage.recoveryHandoffLoggedUsersOnly")}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("agentsListPage.recoveryHandoffLoggedUsersOnlyHelper")}
                    </Typography>
                  </Box>
                }
              />
              {!disableLoggedUsersOnly && loggedUsersOnly ? (
                <LoggedUsersFallbackField
                  fallback={skillConfig.handoff_target?.logged_users_fallback}
                  users={users}
                  agents={handoffAgents}
                  disabled={readOnly}
                  onChange={(nextFallback) =>
                    mergeRecoveryConfig({
                      handoff_target: {
                        ...(skillConfig.handoff_target || {}),
                        logged_users_only: true,
                        logged_users_fallback: nextFallback,
                      },
                    })
                  }
                />
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </RecoveryConfigAccordion>

      <RecoveryConfigAccordion
        icon={CampaignOutlinedIcon}
        title={t("agentsListPage.recoveryChannelsTitle")}
        subtitle={t("agentsListPage.recoveryChannelsHint")}
      >
        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={readOnly}>
          <InputLabel>{t("agentsListPage.recoveryChannelStrategy")}</InputLabel>
          <Select
            label={t("agentsListPage.recoveryChannelStrategy")}
            value={channelStrategy}
            onChange={(e) => mergeRecoveryConfig({ channel_strategy: e.target.value })}
          >
            <MenuItem value="prefer_purchase_channel">{t("agentsListPage.recoveryStrategyPurchase")}</MenuItem>
            <MenuItem value="priority_order">{t("agentsListPage.recoveryStrategyPriority")}</MenuItem>
            <MenuItem value="best_window">{t("agentsListPage.recoveryStrategyWindow")}</MenuItem>
          </Select>
          {strategyHelperKey ? (
            <FormHelperText>{t(strategyHelperKey)}</FormHelperText>
          ) : null}
        </FormControl>

        <Stack spacing={2}>
          {channelRows.map((row, idx) => {
            const channelName =
              channels.find((ch) => Number(ch.id) === Number(row.channel_id))?.nome ||
              channels.find((ch) => Number(ch.id) === Number(row.channel_id))?.name ||
              t("agentsListPage.recoveryChannelUnselected");
            return (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {channelName}
                    </Typography>
                    <Chip size="small" label={t("agentsListPage.recoveryChannelPriorityBadge", { n: row.priority ?? idx + 1 })} />
                  </Stack>
                  {!readOnly ? (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => removeChannel(idx)}
                    >
                      {t("common.remove", { defaultValue: "Remover" })}
                    </Button>
                  ) : null}
                </Stack>
                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small" disabled={readOnly}>
                    <InputLabel>{t("agentsListPage.recoveryChannelSelect")}</InputLabel>
                    <Select
                      label={t("agentsListPage.recoveryChannelSelect")}
                      value={row.channel_id || ""}
                      onChange={(e) => updateChannel(idx, { channel_id: Number(e.target.value) })}
                    >
                      {channels.map((ch) => (
                        <MenuItem key={ch.id} value={ch.id}>
                          {ch.nome || ch.name || ch.id}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{t("agentsListPage.recoveryChannelSelectHelper")}</FormHelperText>
                  </FormControl>
                  <TextField
                    label={t("agentsListPage.recoveryChannelPriority")}
                    helperText={t("agentsListPage.recoveryChannelPriorityHelper")}
                    size="small"
                    value={row.priority ?? idx + 1}
                    onChange={(e) => updateChannel(idx, { priority: e.target.value.replace(/\D/g, "") })}
                    disabled={readOnly}
                    sx={{ maxWidth: 160 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={row.use_free_text_when_window_open !== false}
                        onChange={(e) => updateChannel(idx, { use_free_text_when_window_open: e.target.checked })}
                        disabled={readOnly}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{t("agentsListPage.recoveryFreeTextWhenOpen")}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("agentsListPage.recoveryFreeTextWhenOpenHelper")}
                        </Typography>
                      </Box>
                    }
                  />
                  <ChannelTemplatesFields
                    channelId={row.channel_id}
                    templates={row.templates || {}}
                    onChange={(templates) => updateChannel(idx, { templates })}
                    readOnly={readOnly}
                    t={t}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        {!readOnly ? (
          <Button startIcon={<AddIcon />} onClick={addChannel} sx={{ mt: 1.5 }} size="small" variant="outlined">
            {t("agentsListPage.recoveryAddChannel")}
          </Button>
        ) : null}

        {!channelRows.length ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t("agentsListPage.recoveryNoChannelsWarning")}
          </Alert>
        ) : null}
      </RecoveryConfigAccordion>

      <RecoveryConfigAccordion
        icon={AutoAwesomeOutlinedIcon}
        title={t("agentsListPage.recoveryIntelTitle")}
        subtitle={t("agentsListPage.recoveryIntelHint")}
      >
        <Stack spacing={1.5} divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
          {intelOptions.map(({ key, label, helper, checked }) => (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={checked}
                    onChange={(e) => mergeRecoveryConfig({ [key]: e.target.checked })}
                    disabled={readOnly}
                  />
                }
                label={<Typography variant="body2">{label}</Typography>}
                sx={{ m: 0 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 5.5, lineHeight: 1.45 }}>
                {helper}
              </Typography>
            </Box>
          ))}
        </Stack>
      </RecoveryConfigAccordion>

      <RecoveryConfigAccordion
        icon={ScheduleOutlinedIcon}
        title={t("agentsListPage.faqBusinessHoursTitle")}
        subtitle={t("agentsListPage.recoveryBusinessHoursHint")}
      >
        <FaqBusinessHoursSection
          skillConfig={skillConfig}
          disabled={readOnly}
          embedded
          onChange={(partial) => mergeRecoveryConfig(partial)}
        />
      </RecoveryConfigAccordion>
    </Stack>
  );
}
